import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import LoadingButton from "@/components/ui/LoadingButton";
import IconButton from "@/components/ui/IconButton";
import { iconBtnClass } from "@/components/ui/iconBtnClass";

// 取代 hidden attribute（避免部分瀏覽器對 hidden file input 的 quirk）
const visuallyHiddenStyle = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * 媒體工具列組件 - 統一處理上傳按鈕和權限設定
 * @param {Object} props
 * @param {Function} props.onFileChange - 檔案變更回調
 * @param {Function} props.onVisibilityClick - 權限設定按鈕點擊回調
 * @param {Function} props.onSubmit - 提交按鈕點擊回調
 * @param {Function} props.onCancel - 取消按鈕點擊回調（編輯模式）
 * @param {boolean} props.canSubmit - 是否可以提交
 * @param {string} props.mode - 模式：'create' | 'edit'
 * @param {string} props.submitText - 提交按鈕文字（可選，預設為 'post_verb'）
 * @param {string} props.acceptFiles - 接受的檔案類型（可選）
 * @param {boolean} props.multiple - 是否支援多檔案上傳（可選，預設 true）
 * @param {boolean} props.showImageButton - 是否顯示圖片按鈕（可選，預設 true）
 * @param {boolean} props.showVisibilityButton - 是否顯示權限按鈕（可選，預設 true）
 * @param {boolean} props.showScheduleButton - 是否顯示排程按鈕（可選，預設 true）
 * @param {boolean} props.scheduleDisabled - 排程按鈕是否禁用（貼文已發佈、不再是排程狀態時不允許改時間）
 */
const MediaToolbar = ({
  onFileChange,
  onVisibilityClick,
  onScheduleClick,
  onSubmit,
  onCancel,
  canSubmit = false,
  isLoading = false,
  mode = 'create',
  submitText,
  acceptFiles = "image/png,image/jpg,image/jpeg,video/*",
  multiple = true,
  showImageButton = true,
  showVisibilityButton = true,
  showScheduleButton = true,
  scheduleDisabled = false,
  size = ''
}) => {
  const { t } = useTranslation();
  const btnSizeClass = size === 'sm' ? 'btn-sm' : '';

  return (
    <div className="mt-2">
      <div className="d-flex align-items-center">
        {showImageButton && (
          <OverlayTrigger placement="bottom" overlay={<Tooltip>{t('upload_media')}</Tooltip>}>
            <label className={iconBtnClass()}>
              <FontAwesomeIcon icon={["fas", "photo-film"]} />
              {/* 刻意不設 tabIndex={-1} / aria-hidden：這個 input 是整顆上傳鍵唯一的
                  互動元素，藏起來但必須留在 tab 順序內，鍵盤才按得到（focus 後
                  Enter / Space 會開啟檔案選擇視窗）。外層 label 只有 aria-hidden 的
                  FA svg，給不出名稱，因此 aria-label 掛在 input 上。
                  焦點框由 general.scss 的 .icon-btn:has(:focus-visible) 畫在外層 label */}
              <input
                type="file"
                accept={acceptFiles}
                onChange={onFileChange}
                multiple={multiple}
                style={visuallyHiddenStyle}
                aria-label={t('upload_media')}
              />
            </label>
          </OverlayTrigger>
        )}
        {showVisibilityButton && (
          <IconButton tooltip={t('post_visibility_settings')} placement="bottom" onClick={onVisibilityClick}>
            <FontAwesomeIcon icon={["fas", "user-gear"]} />
          </IconButton>
        )}
        {showScheduleButton && (
          <IconButton tooltip={t('schedule_post')} placement="bottom" onClick={onScheduleClick} disabled={scheduleDisabled}>
            <FontAwesomeIcon icon={["fas", "calendar-check"]} />
          </IconButton>
        )}
      </div>
      <div className="d-flex justify-content-end gap-2">
        {mode === 'edit' && onCancel && (
          <button
            type="button"
            className={`btn rounded-pill btn-neutral ${btnSizeClass}`.trim()}
            onClick={onCancel}
          >
            {t('cancel')}
          </button>
        )}
        <LoadingButton
          className={`btn rounded-pill btn-primaryF ${btnSizeClass}`.trim()}
          onClick={onSubmit}
          disabled={!canSubmit}
          isLoading={isLoading}
        >
          {submitText || t('post_verb')}
        </LoadingButton>
      </div>
    </div>
  );
};

export default MediaToolbar;
